import { IndexedNotification, UserDefinedNotification } from '@apis/Notification/model';
import styled from '@emotion/styled';
import { Alert, Box, Button, Center, Divider, Group, Space, Stack, Text, ThemeIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { useAsync } from '@react-hookz/web';
import { OptionMenuItemTypes } from '@root/Components/Picker/OptionMenu';
import { useCurrentUser, useUser } from '@root/Components/Users/UserHooks';
import { FillerSwitch } from '@root/Design/Filler';
import { SidePanelContentEl, SidePanelPanelEl, SidePanelTitle, SidePanelToolbarEl } from '@root/Design/SidePanel';
import { AuthenticationService } from '@root/Services/AuthenticationService';
import { useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { useFmtSvc } from '@root/Services/FormatService';
import {
    InAppNotificationViewTrackingService,
    UserDefinedNotificationApiService,
} from '@root/Services/Notification/UserDefinedNotificationApiService';
import { queryBuilder } from '@root/Services/QueryExpr';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Asterisk, Clock, InfoCircle, ListCheck, Mail, QuestionMark } from 'tabler-icons-react';
import { describeBaseTrigger } from '../Components/Trigger';
import { NotificationContent, NotificationDescription } from './Design';
import { NotificationSnapshotContent } from './NotificationSnapshotContent';

interface IInAppNotificationViewerProps {
    notificationId: number;
    recipientUserId?: number;
    onShowSettings: (notification: UserDefinedNotification) => void;
    onClose: () => void;
}
export function InAppNotificationViewer(props: IInAppNotificationViewerProps) {
    const { notificationId, onShowSettings, onClose, recipientUserId } = props;
    const authNSvc = useDiMemo(AuthenticationService);

    const viewTrackingSvc = useDiMemo(InAppNotificationViewTrackingService);
    const notificationApi = useDiMemo(UserDefinedNotificationApiService);

    const [{ status, result }, { execute }] = useAsync(async (id: number, userIds?: number[]) => {
        const notificationReq = notificationApi.getNotificationById(id, userIds);
        const indexedRecordReq = queryBuilder<IndexedNotification>()
            .where((b) => b.model.NotificationId!.eq(id))
            .execute((q) => notificationApi.queryNotifications(q))
            .then((r) => r.Results?.[0]);

        const [notification, indexedNotif] = await Promise.all([notificationReq, indexedRecordReq]);
        return { notification, indexedNotif };
    });
    const { notification, indexedNotif } = result ?? {};

    useEffect(() => {
        (async () => {
            await execute(notificationId, recipientUserId ? [recipientUserId] : undefined);
            if (!recipientUserId || recipientUserId === authNSvc.user?.Id) {
                viewTrackingSvc.markNotificationAsRead(notificationId);
            }
        })();
    }, [notificationId, recipientUserId]);

    const handleShowSettings = useCallback(() => {
        if (notification) {
            onShowSettings(notification);
        }
    }, [notification, onShowSettings]);

    return (
        <SidePanelPanelEl>
            <SidePanelTitle title={notification?.NotificationDefinition?.Title ?? 'Notification'} onClose={onClose} />
            <SidePanelContentEl>
                <FillerSwitch loading={status === 'loading'} noData={!notification}>
                    {() =>
                        notification?.NotificationDefinition && notification?.NotificationDataSnapshot ? (
                            <InAppNotificationRenderer notification={notification as ValidNotification} indexedNotification={indexedNotif} />
                        ) : (
                            <InvalidNotification notification={notification} />
                        )
                    }
                </FillerSwitch>
            </SidePanelContentEl>
            <SidePanelToolbarEl>
                <Group sx={{ flex: 1 }} position="apart">
                    <Button onClick={handleShowSettings} variant="outline" disabled={!notification}>
                        Open Settings
                    </Button>
                    <Button onClick={onClose} variant="outline">
                        Close
                    </Button>
                </Group>
            </SidePanelToolbarEl>
        </SidePanelPanelEl>
    );
}

function InvalidNotification({ notification }: { notification?: UserDefinedNotification }) {
    const fmtSvc = useFmtSvc();
    const { Date: date, NotificationDefinition: definition, NotificationDataSnapshot: snapshot } = notification ?? {};
    const title = definition?.Title ?? 'Unknown Notification';
    const description = definition?.Description ?? '';
    const issue = !definition ? 'Invalid notification' : !snapshot ? `No data found for notification` : 'Unknown Notification';
    const additionalDetails = `Notification date: ${!date ? 'Unknown' : fmtSvc.formatDatetime(fmtSvc.toLocalDate(date))}`;
    return (
        <NotificationContent title={title} description={description}>
            <Center sx={{ height: '100%' }}>
                <Text color="dimmed" italic>
                    {issue}
                </Text>
                <Text color="dimmed" size="sm">
                    {additionalDetails}
                </Text>
            </Center>
        </NotificationContent>
    );
}

type ValidNotification = Defined<UserDefinedNotification, 'NotificationDataSnapshot' | 'NotificationDefinition'>;
interface IInAppNotificationRendererProps {
    notification: ValidNotification;
    indexedNotification?: IndexedNotification;
}
function InAppNotificationRenderer({ notification, indexedNotification }: IInAppNotificationRendererProps) {
    const fmtSvc = useFmtSvc();
    const { colors } = useMantineTheme();
    const { user: recipient } = useUser(notification.UserId);
    const { user: owner } = useUser(indexedNotification?.OwnerUserId ?? 0);
    const { user: currUser } = useUser();
    const { NotificationDefinition: def, NotificationDataSnapshot: data } = notification;
    const { Description } = def;

    const deliveryDetails = notification.MessageTypes?.map((m) => {
        const name = m === 'InApp' ? 'in-app' : m === 'Email' ? 'by Email' : '';
        const delivery = indexedNotification?.Delivery?.[m];
        const date = delivery?.Date;
        const successLbl = delivery?.Success === false ? 'failed' : delivery?.Success ? 'at' : 'not sent';
        const dateLbl = !date || !delivery.Success ? '' : fmtSvc.formatMinimalDate(fmtSvc.toLocalDate(date));
        const description = `${successLbl} ${dateLbl}`;
        const unsent = typeof delivery?.Success !== 'boolean';
        const label = `${name} ${description}`;

        return {
            label,
            unsent,
        };
    }).filter((d) => !d.unsent);

    const viewedLbl = !notification?.OpenedAt ? '' : fmtSvc.formatMinimalDate(fmtSvc.toLocalDate(notification?.Date));
    const queriedLbl = !notification?.CreatedAt ? '' : fmtSvc.formatMinimalDate(fmtSvc.toLocalDate(notification?.CreatedAt));
    const { type: triggerDesc } = describeBaseTrigger(indexedNotification?.TriggerType as 'Threshold' | 'Schedule' | undefined);
    const recipientIsCurrUser = !currUser?.Id ? null : recipient?.Id === currUser?.Id;

    // todo: include trigger description
    const menuItems = useMemo(() => new EventEmitter<OptionMenuItemTypes[]>([]), []);

    return (
        <AppNotificationContainer>
            {!Description ? null : (
                <>
                    <NotificationDescription description={Description} />
                    <Space h="md" />
                </>
            )}
            <Group spacing={5} sx={{ fontSize: 13, color: colors.gray[7] }}>
                <Text span>{triggerDesc}</Text>
                <Text span>delivery</Text>
                {!recipient || recipientIsCurrUser ? null : (
                    <Text span>to {`${recipient.fullName} ${recipient?.Status === 'Deactivated' ? '(deactivated)' : ''}`}</Text>
                )}
                {deliveryDetails?.map(({ label }, i) => (
                    <Text span key={i}>
                        {label + (deliveryDetails.length - 1 > i ? ',' : '')}
                    </Text>
                ))}
                {recipientIsCurrUser ? null : <Text span>&mdash;{!viewedLbl ? ' unseen' : ` seen at ${viewedLbl}`}</Text>}
                {!owner || owner?.Id === currUser?.Id || !owner?.fullName ? null : <Text span>authored by {owner.fullName}</Text>}
            </Group>
            <Space h="xs" />
            <Divider color="gray.3" className="--depad-x" />
            <Box className="--depad-x">
                <NotificationSnapshotContent config={def} snapshot={data} menuItems={menuItems} />
            </Box>
            <Divider color="gray.3" className="--depad-x" />
            <Text my="xs" italic color="dimmed" size={14} align="center">
                Display is based on data from {queriedLbl}.
            </Text>
        </AppNotificationContainer>
    );
}

function NotificationVisOptions(props: { menuItems: EventEmitter<OptionMenuItemTypes[]> }) {
    const items = useEventValue(props.menuItems);

    return <></>;
}

const AppNotificationContainer = styled.div`
    --inapp-depadding: calc(-1 * var(--sp-content-padding, 0));
    --inapp-repadding: var(--sp-content-padding, 0);
    margin: var(--inapp-depadding);
    padding: var(--inapp-repadding);
    > .--depad-x {
        margin: 0 var(--inapp-depadding);
    }
`;
